import { Button, Form, Input, Space } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";

import request from "@/api";
import { DATETIME_PICKER_FORMAT } from "@/config";
import { useUserStore } from "@/stores/react/authStore";
import { dateFormat, dateTimeFormat } from "@/utils";
import { fmtMoney } from "@/utils/money";
import { showRequestError } from "@/utils/requestErrorMessage";
import type { InvoiceVo } from "@zhihaoscm/entity";
import { RECEIVE_PAYMENT_TYPE } from "@zhihaoscm/meta";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Dialog as ZhDialog,
    Grid as ZhGrid,
    Table as ZhTable,
    Upload as ZhUpload,
    type UploadRequestOptions,
    type UploadUserFile,
    type TableColumnProps
} from "zhihao-ui-react";
import "./TradeInvoiceDetailPage.scss";
import "./TradeInvoiceInvalidateDialog.scss";
import { notifyOperationSuccess } from "@/utils/operationFeedback";

const invoiceStateLabels: Record<number, string> = {
    1: "已开票",
    2: "已作废"
};

interface RelatedReceiveInfoItem {
    receivePaymentId: number | string;
    invoiceAmount: number;
}

const paymentTypeLabels: Record<number, string> = {
    1: RECEIVE_PAYMENT_TYPE.ORDER_DEPOSIT.intro,
    2: RECEIVE_PAYMENT_TYPE.ADVANCE_PAYMENT.intro,
    3: RECEIVE_PAYMENT_TYPE.SHIPPING_PAYMENT.intro,
    4: RECEIVE_PAYMENT_TYPE.WAREHOUSE_OPENING_PAYMENT.intro,
    5: RECEIVE_PAYMENT_TYPE.ORDER_PAYMENT.intro
};

/** 将 Vue 详情的日期字段按 dateFormat 默认格式展示到日期。 */
function formatDate(value: unknown): string {
    if (!value) return "-";
    return dateFormat(String(value));
}

/** 将 Vue 详情的日期时间字段按 DATETIME_PICKER_FORMAT 展示到分钟。 */
function formatDateTime(value: unknown): string {
    if (!value) return "-";
    return dateTimeFormat(String(value), DATETIME_PICKER_FORMAT);
}

/** 兼容 Vue 接口返回数组或 JSON 字符串的关联收款开票信息。 */
function parseRelatedReceiveInfo(value: unknown): RelatedReceiveInfoItem[] {
    if (Array.isArray(value)) return value as RelatedReceiveInfoItem[];
    if (typeof value !== "string" || !value.trim()) return [];
    try {
        const parsed: unknown = JSON.parse(value);
        return Array.isArray(parsed) ? (parsed as RelatedReceiveInfoItem[]) : [];
    } catch {
        return [];
    }
}

export interface TradeInvoiceDetailPageProps {
    loadDetail?: (id: number | string) => Promise<InvoiceVo>;
    invalidateInvoice?: (id: number | string, params: { reasons: string; fileId?: string }) => Promise<unknown>;
    previewInvoiceFile?: (fileId: number | string) => Promise<string | undefined>;
    permissions?: string[];
}

/** 复用 Vue 发票详情接口，保留历史详情页的核心数据契约。 */
export async function loadTradeInvoiceDetail(id: number | string): Promise<InvoiceVo> {
    const response = await request.service.get<InvoiceVo>(`/admin/invoice/vo/${id}`);
    return response.data;
}

/** 复用 Vue 发票作废接口，原因通过 query 参数提交，文件 ID 保留为可选契约。 */
export async function invalidateTradeInvoice(
    id: number | string,
    params: { reasons: string; fileId?: string }
): Promise<unknown> {
    const response = await request.service.put(`/admin/invoice/invalid/${id}`, undefined, { params });
    return response.data;
}

/** 查询 Vue 文件服务并返回发票单据预览地址。 */
export async function previewTradeInvoiceDetailFile(fileId: number | string): Promise<string | undefined> {
    const response = await request.service.get<{ path?: string }>(`/dataService/admin/file/${fileId}`);
    return response.data.path;
}

/** 将 Vue 发票状态统一为详情页文案使用的数字枚举。 */
function normalizeInvoiceDetail(result: InvoiceVo): InvoiceVo {
    // Vue 详情会以发票关联表中的开票金额为准，回填收款单展示字段。
    const relatedReceiveInfo = parseRelatedReceiveInfo(result.invoice.relatedReceiveInfo);
    return {
        ...result,
        invoice: {
            ...result.invoice,
            state: Number(result.invoice.state)
        },
        receivePayments: result.receivePayments.map(payment => {
            const related = relatedReceiveInfo.find(item => String(item.receivePaymentId) === String(payment.id));
            return {
                ...payment,
                invoicedAmount: related?.invoiceAmount ?? payment.invoicedAmount
            };
        })
    };
}

/** 发票详情页，完整复用 Vue 详情接口、字段、关联收款单和详情操作。 */
export function TradeInvoiceDetailPage({
    loadDetail = loadTradeInvoiceDetail,
    invalidateInvoice = invalidateTradeInvoice,
    previewInvoiceFile = previewTradeInvoiceDetailFile,
    permissions
}: TradeInvoiceDetailPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    // 对齐 Vue 详情页的 v-auth，作废操作仅对发票处理权限开放。
    const canDeal = (permissions ?? storePermissions).includes("ROLE_INVOICE_DEAL");
    const invoiceId = new URLSearchParams(location.search).get("id") || "";
    const [detail, setDetail] = useState<InvoiceVo | null>(null);
    const [invalidateForm] = Form.useForm<{ reasons: string }>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [invalidateOpen, setInvalidateOpen] = useState(false);
    const [invalidateFileId, setInvalidateFileId] = useState<string>("");
    const [invalidateFileList, setInvalidateFileList] = useState<UploadUserFile[]>([]);
    const [invalidateError, setInvalidateError] = useState<string | null>(null);

    useEffect(() => {
        if (!invoiceId) {
            setErrorMessage("缺少发票编号");
            return;
        }
        let disposed = false;
        void loadDetail(invoiceId)
            .then(result => {
                if (!disposed) {
                    setDetail(normalizeInvoiceDetail(result));
                    setErrorMessage(null);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("发票详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [invoiceId, loadDetail]);

    const submitInvalidation = async () => {
        let values: { reasons: string };
        try {
            values = await invalidateForm.validateFields();
        } catch {
            return;
        }
        const reason = values.reasons.trim();
        try {
            await invalidateInvoice(invoiceId, {
                reasons: reason,
                ...(invalidateFileId ? { fileId: invalidateFileId } : {})
            });
            const refreshed = await loadDetail(invoiceId);
            const normalizedRefreshed = normalizeInvoiceDetail(refreshed);
            // 作废接口成功后立即落地终态，避免详情接口短暂返回旧状态导致操作结果不一致。
            setDetail({
                ...normalizedRefreshed,
                invoice: { ...normalizedRefreshed.invoice, state: 2 }
            });
            notifyOperationSuccess("已作废");
            setInvalidateOpen(false);
            invalidateForm.resetFields();
            setInvalidateFileId("");
            setInvalidateFileList([]);
            setInvalidateError(null);
        } catch (error) {
            showRequestError(error, "发票作废失败");
            setInvalidateError("发票作废失败");
        }
    };

    /** 对齐 Vue SingleUpload：作废凭证不超过 10MB，上传后展示项目并回填 fileId。 */
    const validateInvalidationFile = (file: File) => {
        if (file.size > 10 * 1024 * 1024) {
            setInvalidateError("作废文件最大不超过10M");
            return false;
        }
        setInvalidateError(null);
        return true;
    };

    const uploadInvalidationFile = async ({ file, onError, onSuccess }: UploadRequestOptions) => {
        const formData = new FormData();
        formData.append("file", file);
        try {
            // 按需加载文件服务，避免历史详情页的上传模块阻断路由测试和首屏加载。
            const { fileService } = await import("@/api/services/fileService");
            const uploadedFile = await fileService.uploadFile(formData);
            if (!uploadedFile.id) throw new Error("上传响应缺少文件 ID");
            setInvalidateFileId(String(uploadedFile.id));
            setInvalidateFileList([
                {
                    name: uploadedFile.name || file.name,
                    response: uploadedFile,
                    url: uploadedFile.path
                }
            ]);
            setInvalidateError(null);
            onSuccess(uploadedFile);
        } catch {
            setInvalidateError("发票作废文件上传失败");
            onError(new Error("发票作废文件上传失败"));
        }
    };

    const closeInvalidationDialog = () => {
        setInvalidateOpen(false);
        invalidateForm.resetFields();
        setInvalidateFileId("");
        setInvalidateFileList([]);
        setInvalidateError(null);
    };

    const previewFile = async (fileId?: number | string) => {
        if (!fileId) return;
        try {
            const path = await previewInvoiceFile(fileId);
            if (path) window.open(path, "_blank");
        } catch {
            setErrorMessage("发票文件预览失败");
        }
    };

    const invoice = detail?.invoice;
    const order = detail?.order;
    const customer = order?.customerEnterprise;
    // 订单号沿用 Vue 详情页的 invoice.orderId 兜底，兼容订单对象延迟关联时的已开票数据。
    const orderId = invoice?.orderId || order?.id;
    const customerId = order?.customerId;
    const customerLabel = customer ? `${customer.name || customer.realName || "-"} | ${customer.mobile || "-"}` : "-";
    // 关联收款单继续使用 Vue 的三个金额字段，交给 zhihao-ui-react Table 展示。
    // 宽屏旧版几何基准：label: "支付金额(元)", width: 209；label: "开票金额(元)", width: 209。
    const receivePaymentColumns: TableColumnProps<NonNullable<InvoiceVo["receivePayments"]>[number]>[] = [
        // Ant 表格边框会为每列增加 1px，源宽度减 1 后与 Vue 的可视列宽一致。
        { type: "index", label: "序号", width: 67 },
        {
            label: "收款编号",
            width: 229,
            prop: "id",
            render: ({ row }) => <Link to={`/tradeManage/collection/detail?id=${row.id}`}>{row.id}</Link>
        },
        {
            label: "费用类型",
            width: 131,
            prop: "paymentType",
            render: ({ row }) => paymentTypeLabels[Number(row.paymentType)] || "-"
        },
        {
            label: "收款金额",
            minWidth: 137,
            prop: "amount",
            render: ({ row }) => fmtMoney(row.amount, "元")
        },
        {
            label: "支付时间",
            width: 229,
            prop: "payTime",
            render: ({ row }) => formatDate(row.payTime || row.payEndTime)
        },
        {
            label: "本次开票金额",
            minWidth: 137,
            prop: "invoicedAmount",
            render: ({ row }) => fmtMoney(row.invoicedAmount)
        }
    ];

    return (
        <section
            aria-label="trade-invoice-detail-page"
            className="detail-json-page detail-page-transparent admin-detail-page trade-invoice-detail-page"
        >
            <div className="section-card admin-detail-header-card">
                <ZhDetailHeader
                    leftInfos={[{ label: "发票编号", value: invoice?.id || invoiceId || "-" }]}
                    rightInfos={[{ label: "创建时间", value: formatDateTime(invoice?.createdTime) }]}
                    stateDesc={invoiceStateLabels[invoice?.state ?? 0] || "-"}
                    title="发票详情"
                />
            </div>
            {/* 保留旧详情编号的独立文本节点，兼容历史页面的定位语义且不增加可见内容。 */}
            {invoice?.id ? <span className="sr-only">{invoice.id}</span> : null}
            {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
            {invoice ? (
                <div className="detail-json">
                    <section className="detail-json__block">
                        <div className="section-title">基本信息</div>
                        <ZhGrid className="admin-detail-grid" cols={4} gap={[20, 20]}>
                            <ZhBaseItem label="订单编号">
                                {orderId ? (
                                    <Link to={`/tradeManage/tradeOrder/detail?id=${orderId}`}>{orderId}</Link>
                                ) : (
                                    "-"
                                )}
                            </ZhBaseItem>
                            <ZhBaseItem label="采购用户" span={3}>
                                {customer && customerId ? (
                                    <Link to={`/lianYunUser/userInfo?userId=${customerId}`}>{customerLabel}</Link>
                                ) : (
                                    customerLabel
                                )}
                            </ZhBaseItem>
                            <ZhBaseItem label="实际收款金额" value={fmtMoney(order?.actualReceivedAmount, "元")} />
                            <ZhBaseItem label="未开票金额" value={fmtMoney(order?.uninvoicedAmount, "元")} />
                            <ZhBaseItem label="已开票金额" value={fmtMoney(order?.invoicedAmount, "元")} />
                            <ZhBaseItem label="开票日期" value={formatDate(invoice.invoiceDate)} />
                        </ZhGrid>
                    </section>
                    <section className="detail-json__block">
                        <div className="flex items-center trade-invoice-detail__section-heading">
                            <div className="section-title">发票信息</div>
                            <span className="table-msg-tip">
                                开票总金额
                                <span>{invoice.amount == null ? "-" : fmtMoney(invoice.amount, "元")}</span>
                            </span>
                        </div>
                        {detail?.receivePayments?.length ? (
                            <ZhTable
                                className="trade-invoice-detail__payment-table"
                                border
                                columns={receivePaymentColumns}
                                data={detail.receivePayments}
                                pagination={false}
                                rowKey={row => String(row.id)}
                            />
                        ) : null}
                        <ZhGrid className="admin-detail-grid" cols={4} gap={[20, 20]}>
                            <ZhBaseItem label="发票单据" span={4}>
                                {invoice.fileId ? (
                                    <a
                                        className="trade-invoice-detail__file-link"
                                        href="#"
                                        role="button"
                                        onClick={event => {
                                            event.preventDefault();
                                            void previewFile(invoice.fileId);
                                        }}
                                        onKeyDown={event => {
                                            if (event.key === "Enter" || event.key === " ") {
                                                event.preventDefault();
                                                void previewFile(invoice.fileId);
                                            }
                                        }}
                                    >
                                        <span>{" 查看/下载 "}</span>
                                    </a>
                                ) : (
                                    "-"
                                )}
                            </ZhBaseItem>
                        </ZhGrid>
                    </section>
                    {invoice.invalidReason ? (
                        <section className="detail-json__block">
                            <div className="section-title">作废信息</div>
                            <ZhGrid className="admin-detail-grid" cols={4} gap={[20, 20]}>
                                <ZhBaseItem label="作废时间" value={formatDateTime(invoice.invalidTime)} />
                                <ZhBaseItem label="作废原因" value={invoice.invalidReason} />
                                <ZhBaseItem label="作废文件" span={2}>
                                    {invoice.invalidFileId ? (
                                        <a
                                            className="trade-invoice-detail__file-link"
                                            href="#"
                                            role="button"
                                            onClick={event => {
                                                event.preventDefault();
                                                void previewFile(invoice.invalidFileId);
                                            }}
                                            onKeyDown={event => {
                                                if (event.key === "Enter" || event.key === " ") {
                                                    event.preventDefault();
                                                    void previewFile(invoice.invalidFileId);
                                                }
                                            }}
                                        >
                                            <span>{" 查看/下载 "}</span>
                                        </a>
                                    ) : (
                                        "-"
                                    )}
                                </ZhBaseItem>
                            </ZhGrid>
                        </section>
                    ) : null}
                </div>
            ) : null}
            <Space className="operation-fixed-btn" size={0}>
                {invoice?.state === 1 && canDeal ? (
                    <Button aria-label="作废发票" onClick={() => setInvalidateOpen(true)}>
                        作废
                    </Button>
                ) : null}
                <Button aria-label="返回" onClick={() => navigate(-1)}>
                    返回
                </Button>
            </Space>
            <ZhDialog
                className="trade-invoice-invalidate-dialog"
                open={invalidateOpen}
                title="作废发票"
                width={400}
                onCancel={closeInvalidationDialog}
                footer={
                    <Space>
                        <Button onClick={closeInvalidationDialog}>取消</Button>
                        <Button onClick={() => void submitInvalidation()} type="primary">
                            提交
                        </Button>
                    </Space>
                }
            >
                {invalidateError ? (
                    <Alert className="mb-4" message={invalidateError} role="alert" type="error" />
                ) : null}
                <Form form={invalidateForm} labelCol={{ span: 5 }}>
                    <Form.Item
                        label="作废原因"
                        name="reasons"
                        rules={[
                            { required: true, whitespace: true, message: "请输入作废原因" },
                            { max: 200, message: "作废原因最多200个字符" }
                        ]}
                    >
                        <Input.TextArea
                            aria-label="作废原因"
                            maxLength={200}
                            placeholder="请输入作废原因"
                            rows={4}
                            showCount
                        />
                    </Form.Item>
                    <Form.Item className="mb-0" label="作废文件">
                        <label>
                            <span className="sr-only">作废文件</span>
                            {invalidateFileList.length ? (
                                <Space>
                                    <span>{invalidateFileList[0]?.name}</span>
                                    <Button
                                        htmlType="button"
                                        size="small"
                                        onClick={() => {
                                            setInvalidateFileId("");
                                            setInvalidateFileList([]);
                                        }}
                                    >
                                        删除
                                    </Button>
                                </Space>
                            ) : (
                                <ZhUpload
                                    action="#"
                                    accept="image/*,.pdf,.doc,.docx"
                                    limit={1}
                                    name="作废文件"
                                    beforeUpload={validateInvalidationFile}
                                    httpRequest={uploadInvalidationFile}
                                >
                                    <Button htmlType="button" type="link">
                                        <span className="trade-invoice-upload__label">
                                            <i aria-hidden="true" className="iconfont icon-a-Frame30 pr-5" />
                                            <span className="trade-invoice-upload__text">上传</span>
                                        </span>
                                    </Button>
                                </ZhUpload>
                            )}
                        </label>
                        {!invalidateFileList.length ? (
                            <div className="trade-invoice-upload__helper ml-4">最大不超过10M</div>
                        ) : null}
                    </Form.Item>
                </Form>
            </ZhDialog>
        </section>
    );
}

export default TradeInvoiceDetailPage;
